import { Color } from '@angular-material-components/color-picker';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ColorEntityFromColorType, ColorEntityFromNgxMatColor } from '../../../dave-data-module/entities/color.entity';
import { JobSpecificationEntity, JobSpecificationEntityAdditionalData } from '../../../dave-data-module/entities/job-specification.entity';
import { State } from '../../../dave-data-module/State';
import { JobSpecificationActionTypes } from '../../../dave-data-module/State/actions/job-specification.action';
import {
    getQuantityTypes,
    getQuantityTypesFetched,
} from '../../../dave-data-module/State/selectors/accounting.selector';
import { SelectSearchOption } from '../../../dave-utils-module/select-search/components/select-search/select-search.component';
import { DetailListDialogReturn, DetailListTemplateDialogComponent, DetailListTemplateDialogData } from '../../../detail-list-template-dialog/components/detail-list-template-dialog.component';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { DepartmentEntity } from '../../../dave-data-module/entities/department.entity';
import { DepartmentActions } from '../../../dave-data-module/State/actions/department.actions';
import {
    getOwnPartnerOffices,
    getPartnerMainOffice, getPartnerOfficesFetched,
} from '../../../dave-data-module/State/selectors/partners.selectors';
import { getUser } from '../../../dave-data-module/State/selectors/users.selectors';
import {
    getEmployeeByUserId,
    getEmployeesFetched,
} from '../../../dave-data-module/State/selectors/employees.selectors';
import {
    getDepartmentDictionary,
    getDepartmentFetched,
} from '../../../dave-data-module/State/selectors/department.selector';
import { PartnerOfficeResolver } from '../../../dave-data-module/guards/partner-office.resolver';
import { EmployeeResolver } from '../../../dave-data-module/guards/employee.resolver';
import { DepartmentResolver } from '../../../dave-data-module/guards/department.resolver';

export function OpenEditJobspecificationPopup(jsEntity: JobSpecificationEntity, dialog: MatDialog, store: Store<State>, quantityTypeResolver:  { resolve: () => void }) {
    const cName = new FormControl<string>(jsEntity.Name, Validators.required);
    const cUseForCalc = new FormControl<boolean>(jsEntity.UsedInCapacityCalculation);
    const cAmount = new FormControl<number>(jsEntity.Amount);
    const cSellingPrice = new FormControl<number>(jsEntity.SellingPrice);
    const cQuantityTypeId = new FormControl<SelectSearchOption<{ Id: number }> | null>(jsEntity.QuantityTypeId ? { Id: jsEntity.QuantityTypeId } : null);
    const cColor = new FormControl<Color>(jsEntity.AdditionalData?.color ? ColorEntityFromColorType(jsEntity.AdditionalData.color).getNgxMaterialColorPickerObject() : null);

    dialog
        .open<DetailListTemplateDialogComponent, DetailListTemplateDialogData, DetailListDialogReturn>(DetailListTemplateDialogComponent, {
            ...DetailListTemplateDialogComponent.DefaultConfig,
            data: {
                DisableSaveButton$: cName.statusChanges.pipe(
                    startWith(cName.status),
                    map((state) => state !== 'VALID'),
                ),
                Editing: true,
                Data: {
                    Headline: 'Jobbezeichnung bearbeiten',
                    Properties: [
                        {
                            key: 'Bezeichnung',
                            formControl: cName,
                        },
                        {
                            key: 'Farbe',
                            formControl: cColor,
                            options: {
                                specialInput: {
                                    colorPicker: true,
                                },
                            },
                        },
                        {
                            key: 'In Kapazitätsplanung einbeziehen',
                            formControl: cUseForCalc,
                            options: {
                                specialInput: {
                                    boolean: true,
                                },
                            },
                        },
                        {
                            key: 'Kosten (EK)',
                            formControl: cAmount,
                            options: {
                                specialInput: {
                                    number: true,
                                },
                                suffix: '€',
                            },
                        },
                        {
                            key: 'Verkaufspreis (VK)',
                            formControl: cSellingPrice,
                            options: {
                                specialInput: {
                                    number: true,
                                },
                                suffix: '€',
                            },
                        },
                        {
                            key: 'Einheit',
                            formControl: cQuantityTypeId,
                            options: {
                                specialInput: {
                                    singleSelectSearch: {
                                        options$: getFetched$(store, getQuantityTypesFetched, getQuantityTypes, quantityTypeResolver).pipe(map((types) => types?.map((t) => ({ Id: t.Id, optionLabel: [t.Name, t.LangerName].filter(v => !!v).join(' ') })))),
                                        compareOptions: (a, b) => a.Id === b.Id,
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        })
        .afterClosed()
        .subscribe((ret) => {
            if (ret.Action === 'save' && cName.valid && cUseForCalc.valid && cColor.valid && cAmount.valid && cSellingPrice.valid && cQuantityTypeId.valid) {
                const additionalData: JobSpecificationEntityAdditionalData = { ...jsEntity.AdditionalData, color: cColor.value ? ColorEntityFromNgxMatColor(cColor.value) : null };
                store.dispatch(
                    JobSpecificationActionTypes.Modify({
                        Payload: {
                            id: jsEntity.Id,
                            name: cName.value,
                            usedInCapacityCalculation: cUseForCalc.value,
                            additionalData: JSON.stringify(additionalData),
                            amount: cAmount.value,
                            sellingPrice: cSellingPrice.value,
                            quantityTypeId: cQuantityTypeId.value?.Id || null,
                        },
                    }),
                );
            }
        });
}
export function OpenNewJobspecificationPopup(dialog: MatDialog, store: Store<State>): Promise<boolean> {
    const forms = new FormGroup({
        cName: new FormControl<string>('', Validators.required),
        cUseForCalc: new FormControl<boolean>(false),
        cColor: new FormControl<Color>(null),
    });
    return firstValueFrom(
        dialog
            .open<DetailListTemplateDialogComponent, DetailListTemplateDialogData, DetailListDialogReturn>(DetailListTemplateDialogComponent, {
                ...DetailListTemplateDialogComponent.DefaultConfig,
                data: {
                    DisableSaveButton$: forms.statusChanges.pipe(
                        startWith(forms.status),
                        map((state) => state !== 'VALID'),
                    ),
                    DeleteButton: true,
                    Editing: true,
                    Data: {
                        Headline: 'Jobbezeichnung anlegen',
                        Properties: [
                            {
                                key: 'Bezeichnung',
                                formControl: forms.controls.cName,
                            },
                            {
                                key: 'Farbe',
                                formControl: forms.controls.cColor,
                                options: {
                                    specialInput: {
                                        colorPicker: true,
                                    },
                                },
                            },
                            {
                                key: 'In Kapazitätsplanung einbeziehen',
                                formControl: forms.controls.cUseForCalc,
                                options: {
                                    specialInput: {
                                        boolean: true,
                                    },
                                },
                            },
                        ],
                    },
                },
            })
            .afterClosed(),
    ).then((ret) => {
        if (ret.Action === 'save' && forms.valid) {
            const additionalData: JobSpecificationEntityAdditionalData = { color: forms.value.cColor ? ColorEntityFromNgxMatColor(forms.value.cColor) : null };
            store.dispatch(JobSpecificationActionTypes.Create({ Payload: { name: forms.value.cName, usedInCapacityCalculation: forms.value.cUseForCalc, additionalData: JSON.stringify(additionalData) } }));
            return true;
        } else {
            return false;
        }
    });
}


export function OpenEditDepartmentPopup(jsEntity: DepartmentEntity, dialog: MatDialog, store: Store<State>, departmentLabel: string) {
    const cName = new FormControl<string>(jsEntity.Name, Validators.required);
    const cSpartennummer = new FormControl<string>(jsEntity.Spartennummer);

    dialog
        .open<DetailListTemplateDialogComponent, DetailListTemplateDialogData, DetailListDialogReturn>(DetailListTemplateDialogComponent, {
            ...DetailListTemplateDialogComponent.DefaultConfig,
            data: {
                DisableSaveButton$: cName.statusChanges.pipe(
                    startWith(cName.status),
                    map((state) => state !== 'VALID'),
                ),
                Editing: true,
                Data: {
                    Headline: departmentLabel + ' bearbeiten',
                    Properties: [
                        {
                            key: DepartmentEntity.PropertyNames.Name,
                            formControl: cName,
                        },
                        {
                            key: DepartmentEntity.PropertyNames.Spartennummer,
                            formControl: cSpartennummer,
                        },
                    ],
                },
            },
        })
        .afterClosed()
        .subscribe((ret) => {
            if (ret.Action === 'save' && cName.valid && cSpartennummer.valid) {
                store.dispatch(
                    DepartmentActions.changeRequest({
                        Payload: {
                            Id: jsEntity.Id.toString(),
                            Name: cName.value,
                            Spartennummer: cSpartennummer.value,

                            // ist im request required...
                            PartnerOfficeId: jsEntity.PartnerOfficeId.toString(),
                        },
                    }),
                );
            }
        });
}
export async function OpenNewDepartmentPopup(
        dialog: MatDialog,
        store: Store<State>,
        departmentLabel: string,
        partnerOfficeResolver: PartnerOfficeResolver,
        employeeResolver: EmployeeResolver,
        departmentsResolver: DepartmentResolver,
    ): Promise<boolean> {
    console.log('OpenNewDepartmentPopup');
    const partnerMainOffice = await firstValueFrom(getFetched$(store, getPartnerOfficesFetched, getPartnerMainOffice));
    const ownPartnerOffices = await firstValueFrom(getFetched$(store, getPartnerOfficesFetched, getOwnPartnerOffices, partnerOfficeResolver));
    const user = await firstValueFrom(store.select(getUser));
    const employee = await firstValueFrom(getFetched$(store, getEmployeesFetched, getEmployeeByUserId({userId: user.Id}), employeeResolver));
    const departments = await firstValueFrom(getFetched$(store, getDepartmentFetched, getDepartmentDictionary, departmentsResolver));
    console.log('OpenNewDepartmentPopup, resolved');

    const forms = new FormGroup({
        cName: new FormControl<string>('', Validators.required),
        cSpartennummer: new FormControl<string>(''),
        cPartnerOfficeId: new FormControl<number>(employee.DepartmentId ? departments[employee.DepartmentId].PartnerOfficeId : partnerMainOffice.Id, Validators.required),
    });
    return firstValueFrom(
        dialog
            .open<DetailListTemplateDialogComponent, DetailListTemplateDialogData, DetailListDialogReturn>(DetailListTemplateDialogComponent, {
                ...DetailListTemplateDialogComponent.DefaultConfig,
                data: {
                    DisableSaveButton$: forms.statusChanges.pipe(
                        startWith(forms.status),
                        map((state) => state !== 'VALID'),
                    ),
                    DeleteButton: true,
                    Editing: true,
                    Data: {
                        Headline: departmentLabel + ' anlegen',
                        Properties: [
                            {
                                key: DepartmentEntity.PropertyNames.Name,
                                formControl: forms.controls.cName,
                            },
                            {
                                key: DepartmentEntity.PropertyNames.Spartennummer,
                                formControl: forms.controls.cSpartennummer,
                            },
                            {
                                key: DepartmentEntity.PropertyNames.PartnerOfficeId,
                                formControl: forms.controls.cPartnerOfficeId,
                                hideFormControl: ownPartnerOffices.length < 2,
                                options: {
                                    specialInput: {
                                        select: ownPartnerOffices?.map((c) => ({
                                            optionValue: c.Id,
                                            optionLabel: c.Name,
                                        })),
                                    },
                                },
                            },
                        ],
                    },
                },
            })
            .afterClosed(),
    ).then((ret) => {
        if (ret?.Action === 'save' && forms.valid) {
            store.dispatch(DepartmentActions.addRequest({Payload: {
                Name: forms.controls.cName.value,
                Spartennummer: forms.controls.cSpartennummer.value,
                PartnerOfficeId: forms.controls.cPartnerOfficeId.value.toString(),
                }
            }));
            return true;
        } else {
            return false;
        }
    });
}
